import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  FaCheckCircle, FaTimes, FaSpinner, FaPaperclip, FaFileImage, FaTruck,
  FaMapMarkerAlt, FaGlobeAfrica, FaBox, FaWeightHanging, FaExternalLinkAlt, FaUpload
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import Modal from '../components/Modal';
import { API_URL } from '../config';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

const STATUS_META = {
  pending: { label: 'Awaiting approval', color: '#b45309', bg: '#fffbeb' },
  approved: { label: 'Approved', color: '#16a34a', bg: '#f0fdf4' },
  rejected: { label: 'Rejected', color: '#dc2626', bg: '#fef2f2' }
};

const TABS = [
  { key: 'pending', label: 'Awaiting approval' },
  { key: 'decided', label: 'Approved / rejected' },
  { key: 'all', label: 'All PODs' }
];

function PodApprovalsPage({ onLogout }) {
  const [user] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [tab, setTab] = useState('pending');
  const [pods, setPods] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [noteTarget, setNoteTarget] = useState(null); // { pod, approved }
  const [note, setNote] = useState('');
  const [attachTarget, setAttachTarget] = useState(null);
  const [attach, setAttach] = useState({ pod_number: '', pod_receiver_name: '', pod_weight_mt: '', pod_image_url: '' });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    load(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const load = async (which = tab) => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/pod-approvals?status=${which}`, { headers: authHeaders() });
      const data = await r.json();
      if (data.success) {
        setPods(data.pods || []);
        setCounts(data.counts || { pending: 0, approved: 0, rejected: 0 });
      } else {
        setError(data.error || 'Could not load the POD queue.');
      }
    } catch (err) {
      console.error('POD queue error:', err);
      setError('Could not load the POD queue.');
    } finally {
      setLoading(false);
    }
  };

  const decide = async (pod, approved, notes = '') => {
    setBusyId(pod.id);
    try {
      const r = await fetch(`${API_URL}/fleet/fleet-allocations/${pod.id}/pod/approve`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved, notes, type: 'POD' })
      });
      const data = await r.json();
      if (data.success) {
        toast.success(approved ? '✅ POD approved — the load is marked completed' : 'POD rejected');
        setNoteTarget(null);
        setNote('');
        load();
      } else {
        toast.error(data.error || 'Could not update the POD');
      }
    } catch (err) {
      console.error('POD decision error:', err);
      toast.error('Could not update the POD');
    } finally {
      setBusyId(null);
    }
  };

  const pickFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/uploads/pod`, { method: 'POST', headers: authHeaders(), body: fd });
      const data = await r.json();
      const url = data && data.file && data.file.url;
      if (!url) throw new Error((data && data.error) || 'Upload failed');
      setAttach((prev) => ({ ...prev, pod_image_url: url }));
      toast.success('📄 Document uploaded');
    } catch (err) {
      console.error('POD upload error:', err);
      toast.error(err.message || 'Could not upload the document');
    } finally {
      setUploading(false);
    }
  };

  const saveAttachment = async () => {
    if (!attachTarget) return;
    if (!attach.pod_number || !attach.pod_receiver_name || !attach.pod_weight_mt) {
      toast.error('POD number, receiver name and weight are required');
      return;
    }
    setBusyId(attachTarget.id);
    try {
      const r = await fetch(`${API_URL}/fleet/fleet-allocations/${attachTarget.id}/pod`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pod_number: attach.pod_number,
          pod_receiver_name: attach.pod_receiver_name,
          pod_weight_mt: Number(attach.pod_weight_mt),
          pod_image_url: attach.pod_image_url || null
        })
      });
      const data = await r.json();
      if (data.success) {
        toast.success('📄 POD attached — it is now awaiting your approval');
        setAttachTarget(null);
        setAttach({ pod_number: '', pod_receiver_name: '', pod_weight_mt: '', pod_image_url: '' });
        load();
      } else {
        toast.error(data.error || 'Could not attach the POD');
      }
    } catch (err) {
      console.error('POD attach error:', err);
      toast.error('Could not attach the POD');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <AdminLayout activeTab="pod-approvals" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-navy">📄 POD Approvals</h1>
          <p className="text-sm text-gray-500">
            Delivery documents uploaded by subbies or the office. Approving one marks its load completed — the client sees the POD immediately.
          </p>
        </div>
        <button
          onClick={() => load()}
          className="px-4 py-2 rounded-lg border text-sm font-medium hover:bg-gray-50"
          style={{ borderColor: '#E8ECEF' }}
        >
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className="px-4 py-2 rounded-lg text-sm font-semibold border flex items-center gap-2"
            style={{
              borderColor: tab === t.key ? '#00A86B' : '#E8ECEF',
              backgroundColor: tab === t.key ? '#f0fdf4' : '#ffffff',
              color: tab === t.key ? '#047857' : '#4A5568'
            }}
          >
            {t.label}
            {t.key === 'pending' && counts.pending > 0 ? (
              <span className="px-1.5 py-0.5 rounded-full text-[10px] text-white" style={{ backgroundColor: '#dc2626' }}>{counts.pending}</span>
            ) : null}
          </button>
        ))}
      </div>

      {error ? (
        <div className="rounded-xl border p-4 mb-6 text-sm" style={{ borderColor: '#fecaca', backgroundColor: '#fef2f2', color: '#b91c1c' }}>{error}</div>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-3xl mx-auto mb-4" style={{ color: '#00A86B' }} />
            <p className="text-gray-500">Loading PODs...</p>
          </div>
        </div>
      ) : pods.length === 0 ? (
        <div className="bg-white rounded-xl border p-16 text-center" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-4xl mb-4">📭</div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#0A1929' }}>
            {tab === 'pending' ? 'No PODs are waiting for approval' : 'Nothing to show here yet'}
          </h3>
          <p className="text-sm text-gray-500">
            When a subbie — or the office — uploads a delivery document it lands in this queue.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {pods.map((pod) => {
            const meta = STATUS_META[pod.pod_status] || STATUS_META.pending;
            const busy = busyId === pod.id;
            return (
              <div key={pod.id} className="bg-white rounded-xl border p-5" style={{ borderColor: '#E8ECEF' }}>
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold" style={{ color: '#0A1929' }}>{pod.pod_number || 'POD number not captured'}</span>
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ color: meta.color, backgroundColor: meta.bg }}>{meta.label}</span>
                      {pod.cross_border ? (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1" style={{ color: '#b45309', backgroundColor: '#fffbeb' }}>
                          <FaGlobeAfrica size={9} /> Cross-border
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                      {[pod.job_number, pod.load_number, pod.leg_label].filter(Boolean).join(' · ') || 'Load details not linked'}
                    </p>
                  </div>
                  <div className="text-right text-xs" style={{ color: '#4A5568' }}>
                    <p className="font-semibold" style={{ color: '#0A1929' }}>{pod.client_name || '—'}</p>
                    {pod.subcontractor ? (
                      <p className="flex items-center gap-1 justify-end"><FaTruck size={10} /> {pod.subcontractor}</p>
                    ) : null}
                    {pod.truck ? <p>Truck {pod.truck}</p> : null}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs mb-4">
                  <div className="rounded-lg p-3 sm:col-span-2" style={{ backgroundColor: '#f8fafc' }}>
                    <p className="uppercase tracking-wide mb-0.5 flex items-center gap-1" style={{ color: '#94A3B8' }}>
                      <FaMapMarkerAlt size={10} /> Route
                    </p>
                    <p style={{ color: '#0A1929' }}>{pod.route || '—'}</p>
                  </div>
                  <div className="rounded-lg p-3" style={{ backgroundColor: '#f8fafc' }}>
                    <p className="uppercase tracking-wide mb-0.5" style={{ color: '#94A3B8' }}>Delivered</p>
                    <p style={{ color: '#0A1929' }}>{fmtDateTime(pod.delivered_at)}</p>
                  </div>
                  <div className="rounded-lg p-3" style={{ backgroundColor: '#f8fafc' }}>
                    <p className="uppercase tracking-wide mb-0.5" style={{ color: '#94A3B8' }}>Received by</p>
                    <p style={{ color: '#0A1929' }}>{pod.receiver || '—'}</p>
                  </div>
                  <div className="rounded-lg p-3" style={{ backgroundColor: '#f8fafc' }}>
                    <p className="uppercase tracking-wide mb-0.5 flex items-center gap-1" style={{ color: '#94A3B8' }}>
                      <FaWeightHanging size={10} /> Weight
                    </p>
                    <p style={{ color: '#0A1929' }}>{pod.weight_mt != null ? `${pod.weight_mt} MT` : '—'}</p>
                  </div>
                  <div className="rounded-lg p-3" style={{ backgroundColor: '#f8fafc' }}>
                    <p className="uppercase tracking-wide mb-0.5 flex items-center gap-1" style={{ color: '#94A3B8' }}>
                      <FaBox size={10} /> Commodity
                    </p>
                    <p style={{ color: '#0A1929' }}>{pod.commodity || '—'}</p>
                  </div>
                  <div className="rounded-lg p-3" style={{ backgroundColor: '#f8fafc' }}>
                    <p className="uppercase tracking-wide mb-0.5" style={{ color: '#94A3B8' }}>Uploaded</p>
                    <p style={{ color: '#0A1929' }}>{fmtDateTime(pod.uploaded_at)}</p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {pod.image_url ? (
                    <a
                      href={pod.image_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold hover:bg-gray-50"
                      style={{ borderColor: '#E8ECEF', color: '#0A1929' }}
                    >
                      <FaFileImage size={12} /> View document <FaExternalLinkAlt size={9} className="opacity-60" />
                    </a>
                  ) : (
                    <span className="text-xs" style={{ color: '#b45309' }}>No document attached yet</span>
                  )}

                  <button
                    onClick={() => {
                      setAttachTarget(pod);
                      setAttach({
                        pod_number: pod.pod_number || '',
                        pod_receiver_name: pod.receiver || '',
                        pod_weight_mt: pod.weight_mt != null ? String(pod.weight_mt) : '',
                        pod_image_url: pod.image_url || ''
                      });
                    }}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold hover:bg-gray-50"
                    style={{ borderColor: '#E8ECEF', color: '#0A1929' }}
                  >
                    <FaPaperclip size={12} /> {pod.image_url ? 'Replace / edit POD' : 'Attach POD'}
                  </button>

                  {pod.pod_status === 'pending' ? (
                    <>
                      <button
                        disabled={busy}
                        onClick={() => decide(pod, true)}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold disabled:opacity-50"
                        style={{ backgroundColor: '#00A86B' }}
                      >
                        {busy ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />} Approve POD
                      </button>
                      <button
                        disabled={busy}
                        onClick={() => { setNoteTarget({ pod, approved: false }); setNote(''); }}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        <FaTimes /> Reject / query
                      </button>
                    </>
                  ) : (
                    <button
                      disabled={busy}
                      onClick={() => { setNoteTarget({ pod, approved: true }); setNote(''); }}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold hover:bg-gray-50 disabled:opacity-50"
                      style={{ borderColor: '#E8ECEF', color: '#4A5568' }}
                    >
                      <FaCheckCircle /> Change decision
                    </button>
                  )}

                </div>
              </div>
            );
          })}

        </div>
      )}

      {/* ── Approve / reject with a note ── */}
      <Modal
        isOpen={!!noteTarget}
        onClose={() => { if (!busyId) { setNoteTarget(null); setNote(''); } }}
        title={noteTarget && noteTarget.approved ? 'Approve POD' : 'Reject / query POD'}
      >
        {noteTarget ? (
          <div>
            <p className="text-sm text-gray-600 mb-4">
              {noteTarget.approved
                ? `Approve the POD for ${noteTarget.pod.pod_number || noteTarget.pod.job_number || 'this load'}? The load is marked completed and the client can download the document.`
                : `Send the POD for ${noteTarget.pod.pod_number || noteTarget.pod.job_number || 'this load'} back with a query. Please say what is wrong so the subbie can fix it.`}
            </p>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={noteTarget.approved ? 'Note (optional)...' : 'What is wrong with this POD?'}
              className="w-full px-3 py-2 border rounded-lg text-sm mb-6 focus:outline-none"
              style={{ borderColor: '#E8ECEF' }}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setNoteTarget(null); setNote(''); }}
                disabled={!!busyId}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => decide(noteTarget.pod, noteTarget.approved, note)}
                disabled={!!busyId}
                className="px-6 py-2 rounded-lg text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: noteTarget.approved ? '#00A86B' : '#dc2626' }}
              >
                {busyId ? <FaSpinner className="animate-spin" /> : (noteTarget.approved ? <FaCheckCircle /> : <FaTimes />)}
                {noteTarget.approved ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* ── Attach / replace the POD document ── */}
      <Modal
        isOpen={!!attachTarget}
        onClose={() => { if (!busyId && !uploading) setAttachTarget(null); }}
        title="Attach POD document"
      >
        {attachTarget ? (
          <div>
            <p className="text-sm text-gray-600 mb-4">
              {attachTarget.pod_number ? `Updating ${attachTarget.pod_number}` : 'Capture the delivery document for this load.'}
              {attachTarget.job_number ? ` · ${attachTarget.job_number}` : ''}
              {attachTarget.client_name ? ` · ${attachTarget.client_name}` : ''}
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">POD number</label>
                <input
                  value={attach.pod_number}
                  onChange={(e) => setAttach((p) => ({ ...p, pod_number: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none"
                  style={{ borderColor: '#E8ECEF' }}
                  placeholder="e.g. 4521"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Received by</label>
                <input
                  value={attach.pod_receiver_name}
                  onChange={(e) => setAttach((p) => ({ ...p, pod_receiver_name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none"
                  style={{ borderColor: '#E8ECEF' }}
                  placeholder="Name on the delivery note"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Delivered weight (MT)</label>
                <input
                  type="number"
                  step="0.001"
                  value={attach.pod_weight_mt}
                  onChange={(e) => setAttach((p) => ({ ...p, pod_weight_mt: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none"
                  style={{ borderColor: '#E8ECEF' }}
                  placeholder="e.g. 32.5"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Scanned document</label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => pickFile(e.target.files && e.target.files[0])}
                  className="w-full text-sm"
                />
                {uploading ? <p className="text-xs text-gray-500 mt-1">Uploading...</p> : null}
                {attach.pod_image_url ? (
                  <p className="text-xs mt-1" style={{ color: '#16a34a' }}>
                    Attached:{' '}
                    <a href={attach.pod_image_url} target="_blank" rel="noopener noreferrer" className="underline">
                      view the file
                    </a>
                  </p>
                ) : null}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setAttachTarget(null)}
                disabled={!!busyId || uploading}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={saveAttachment}
                disabled={!!busyId || uploading}
                className="px-6 py-2 rounded-lg text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: '#00A86B' }}
              >
                {busyId ? <FaSpinner className="animate-spin" /> : <FaUpload size={12} />} Save POD
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

    </AdminLayout>
  );
}

export default PodApprovalsPage;
